import UTMTracker from "./UTMTracker";
import { track, EVENTS } from "./analyticsService";
import { APP_STORE_IDS, appStoreIdFor, smartBannerContent } from "../seo/appStores";

// App Store app ids per store, and the Smart App Banner content (src/seo/appStores.js).
export { APP_STORE_IDS, appStoreIdFor, smartBannerContent };

/**
 * "Get the app" links. Every Play / App Store link in the web app comes from
 * here so each one lands on the Fleek listing (never a store search) and says
 * where it was clicked.
 *
 * Play: the source rides in `referrer`, which the app reads through the
 * Install Referrer API and Play Console reports as the install campaign. If
 * this browser first arrived with UTMs (first touch, see UTMTracker), those
 * are carried through and the placement goes in utm_content, so an
 * Instagram -> web -> Play install still credits Instagram.
 *
 * App Store: `ct` campaign token, web_app_<placement>.
 *
 * appLink(): the backend's one "get the app" link (api.getfleek.app/app),
 * for placements that name their own utm tags. It counts the click as
 * app_link_clicked and redirects to Play or the App Store by user agent,
 * with the same tags (backend misc/app_link.py).
 *
 * Placements are stable snake_case literals; add new ones here.
 */

export const STORES = {
  PLAY: "play",
  APP_STORE: "app_store",
};

export const PLACEMENTS = {
  FLEEK_TAB_HERO: "fleek_tab_hero",
  FLEEK_TAB_CTA: "fleek_tab_cta",
  MARKETPLACE: "marketplace",
  FOOTER: "footer",
  PLAY_BADGE: "play_badge",
  INSTALL_BANNER: "install_banner",
  MANAGE_POPUP: "manage_popup",
  MANAGE_POPUP_CTA: "manage_popup_cta",
  SMART_BANNER: "smart_banner",
};

const PLAY_LISTING = "https://play.google.com/store/apps/details?id=com.fleek.app";
const APP_STORE_LISTING = `https://apps.apple.com/in/app/fleek-manage-subscriptions/id${APP_STORE_IDS.IN}`;
const APP_LINK = `${(process.env.REACT_APP_API_URL || "https://api.getfleek.app").replace(/\/$/, "")}/app`;

const enc = encodeURIComponent;

// The visitor's original source, or null when the first visit had no UTMs.
const firstTouchUtms = () => {
  const ft = UTMTracker.getFirstTouch();
  if (!ft || !ft.utm_source) return null;
  return {
    utm_source: ft.utm_source,
    utm_medium: ft.utm_medium,
    utm_campaign: ft.utm_campaign,
  };
};

// The Play referrer, before it is encoded as a whole into the listing URL.
// Values are encoded individually first so a stray & or = cannot split them.
export const playReferrer = (placement) => {
  const ft = firstTouchUtms();
  const pairs = ft
    ? [
        ["utm_source", ft.utm_source],
        ["utm_medium", ft.utm_medium],
        ["utm_campaign", ft.utm_campaign],
        ["utm_content", `web_app_${placement}`],
      ]
    : [
        ["utm_source", "web_app"],
        ["utm_medium", placement],
      ];
  return pairs
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${enc(v)}`)
    .join("&");
};

/** The listing URL for `store` ("play" | "app_store"), tagged with `placement`. */
export const storeLink = (store, placement) => {
  if (store === STORES.APP_STORE) {
    return `${APP_STORE_LISTING}?ct=${enc(`web_app_${placement}`)}`;
  }
  return `${PLAY_LISTING}&referrer=${enc(playReferrer(placement))}`;
};

/**
 * api.getfleek.app/app with these utm tags (utm_source and utm_medium, plus
 * utm_campaign / utm_content when given; empty ones are left out).
 */
export const appLink = (utm = {}) => {
  const query = ["utm_source", "utm_medium", "utm_campaign", "utm_content"]
    .filter((key) => utm[key])
    .map((key) => `${key}=${enc(utm[key])}`)
    .join("&");
  return query ? `${APP_LINK}?${query}` : APP_LINK;
};

/** Fire store_link_clicked. Never blocks the navigation. */
export const trackStoreLinkClick = (store, placement) =>
  track(EVENTS.STORE_LINK_CLICKED, { store, placement });

/**
 * Spread onto an <a>: href + click tracking. The href is computed at render,
 * so it reflects the first touch stored on this browser.
 */
export const storeLinkProps = (store, placement) => ({
  href: storeLink(store, placement),
  onClick: () => trackStoreLinkClick(store, placement),
});
